<template>
  <div>
    <Head :title="`Cierre de Caja - ${cashClosure.cash_register?.name}`" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <div class="flex items-center gap-2">
            <h1 class="text-3xl font-bold">{{ (cashClosure.notes || '').includes('[ARQUEO PARCIAL]') ? 'Arqueo de Caja' : 'Cierre de Caja' }}</h1>
            <span
              v-if="(cashClosure.notes || '').includes('[ARQUEO PARCIAL]')"
              class="px-2.5 py-1 rounded text-xs font-black bg-indigo-100 text-indigo-800 border border-indigo-200"
            >
              Arqueo Parcial
            </span>
            <span
              v-else
              class="px-2.5 py-1 rounded text-xs font-black bg-teal-100 text-teal-800 border border-teal-200"
            >
              Cierre Definitivo
            </span>
          </div>
          <p class="text-gray-600 mt-1">{{ cashClosure.cash_register?.name }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-3">
        <a
          :href="`/cash-closures/${cashClosure.id}/report`"
          class="btn-blue flex items-center gap-2"
          target="_blank"
        >
          <i class="fa fa-file-pdf mr-1"></i>
          Descargar PDF
        </a>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <!-- Opening Balance -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-sm font-medium text-gray-500 mb-2">Saldo Inicial</h3>
        <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(cashClosure.opening_balance) }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.opening_balance_bs, 'VES') }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.opening_balance_cop, 'COP') }}</p>
      </div>

      <!-- Expected Balance -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-sm font-medium text-gray-500 mb-2">Saldo Esperado</h3>
        <p class="text-2xl font-bold text-blue-600">{{ formatCurrency(cashClosure.expected_balance) }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.expected_balance_bs, 'VES') }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.expected_balance_cop, 'COP') }}</p>
      </div>

      <!-- Actual Balance -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-sm font-medium text-gray-500 mb-2">Saldo Real</h3>
        <p class="text-2xl font-bold text-green-600">{{ formatCurrency(cashClosure.closing_balance) }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.closing_balance_bs, 'VES') }}</p>
        <p class="text-sm text-gray-500">{{ formatCurrency(cashClosure.closing_balance_cop, 'COP') }}</p>
      </div>

      <!-- Difference -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-sm font-medium text-gray-500 mb-2">Diferencia</h3>
        <p
          :class="cashClosure.difference >= 0 ? 'text-green-600' : 'text-red-600'"
          class="text-2xl font-bold"
        >
          {{ formatCurrency(cashClosure.difference) }}
        </p>
        <p
          :class="cashClosure.difference_bs >= 0 ? 'text-green-600' : 'text-red-600'"
          class="text-sm"
        >
          {{ formatCurrency(cashClosure.difference_bs, 'VES') }}
        </p>
        <p
          :class="cashClosure.difference_cop >= 0 ? 'text-green-600' : 'text-red-600'"
          class="text-sm"
        >
          {{ formatCurrency(cashClosure.difference_cop, 'COP') }}
        </p>
      </div>
    </div>

    <!-- Details -->
    <div class="bg-white rounded-lg shadow-sm mb-6">
      <div class="flex items-center px-6 py-4 bg-teal-800 text-white rounded-t-lg">
        <h2 class="text-lg font-semibold">Detalles del Cierre</h2>
      </div>

      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 class="text-sm font-medium text-gray-500">Fecha de Cierre</h3>
            <p class="text-sm text-gray-900">{{ formatDate(cashClosure.closure_date) }}</p>
          </div>

          <div>
            <h3 class="text-sm font-medium text-gray-500">{{ (cashClosure.notes || '').includes('[ARQUEO PARCIAL]') ? 'Arqueado por' : 'Cerrado por' }}</h3>
            <p class="text-sm font-bold text-gray-900">
              {{ cashClosure.closed_by?.name || (cashClosure.closed_by ? (cashClosure.closed_by.first_name + ' ' + (cashClosure.closed_by.last_name || '')) : (cashClosure.closedBy?.name || 'N/A')) }}
            </p>
          </div>
        </div>

        <div v-if="cashClosure.notes" class="mt-6 pt-6 border-t border-gray-200">
          <h3 class="text-sm font-medium text-gray-500 mb-2">Notas</h3>
          <p class="text-sm text-gray-900">{{ cashClosure.notes }}</p>
        </div>
      </div>
    </div>

    <!-- Detalle de Retiros de Efectivo (Egresos) -->
    <div class="bg-white rounded-lg shadow-sm mb-6 border border-red-100 overflow-hidden">
      <div class="flex items-center justify-between px-6 py-4 bg-red-700 text-white">
        <h2 class="text-lg font-bold flex items-center">
          <i class="fa fa-hand-holding-usd mr-2"></i> Retiros de Efectivo del Turno
        </h2>
        <span class="text-xs bg-red-800 px-3 py-1 rounded-full font-semibold">
          {{ withdrawals.length }} {{ withdrawals.length === 1 ? 'Retiro' : 'Retiros' }}
        </span>
      </div>

      <div class="p-6">
        <div v-if="withdrawals.length > 0" class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead class="bg-red-50 text-red-900 font-bold border-b border-red-100">
              <tr>
                <th class="p-3">Fecha / Hora</th>
                <th class="p-3">Motivo / Concepto</th>
                <th class="p-3">Autorizado Por</th>
                <th class="p-3 text-right">USD</th>
                <th class="p-3 text-right">Bolívares (Bs)</th>
                <th class="p-3 text-right">Pesos (COP)</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="w in withdrawals" :key="w.id" class="hover:bg-red-50/40">
                <td class="p-3 text-gray-600">{{ formatDate(w.created_at) }}</td>
                <td class="p-3 font-semibold text-gray-800">{{ w.description || 'Retiro de efectivo' }}</td>
                <td class="p-3 text-gray-600">
                  {{ w.responsible ? (w.responsible.first_name + ' ' + w.responsible.last_name) : (w.responsible?.name || 'Usuario') }}
                </td>
                <td class="p-3 text-right font-bold text-red-600">${{ Number(w.amount || 0).toFixed(2) }}</td>
                <td class="p-3 text-right font-bold text-red-600">Bs. {{ Number(w.amount_bs || 0).toFixed(2) }}</td>
                <td class="p-3 text-right font-bold text-red-600">COP {{ Math.round(Number(w.amount_cop || 0)).toLocaleString('es-CO') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-sm text-gray-500 italic py-2">
          No se registraron retiros de dinero durante este turno.
        </div>
      </div>
    </div>

    <!-- Transactions Summary -->
    <div v-if="cashClosure.transactions_summary" class="bg-white rounded-lg shadow-sm">
      <div class="flex items-center px-6 py-4 bg-teal-800 text-white rounded-t-lg">
        <h2 class="text-lg font-semibold">Resumen de Transacciones</h2>
      </div>

      <div class="p-6">
        <div class="overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <thead>
              <tr class="text-center bg-gray-50">
                <th class="p-3 font-semibold">Tipo</th>
                <th class="p-3 font-semibold">Cantidad</th>
                <th class="p-3 font-semibold">Total USD</th>
                <th class="p-3 font-semibold">Total BS</th>
                <th class="p-3 font-semibold">Total COP</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(summary, type) in cashClosure.transactions_summary"
                :key="type"
                class="border-t border-gray-100 text-center"
              >
                <td class="p-3">
                  <span
                    :class="getTransactionTypeClass(type)"
                    class="px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {{ getTransactionTypeText(type) }}
                  </span>
                </td>
                <td class="p-3 text-gray-600">{{ summary.count }}</td>
                <td class="p-3 text-gray-600">{{ formatCurrency(summary.total_usd) }}</td>
                <td class="p-3 text-gray-600">{{ formatCurrency(summary.total_bs, 'VES') }}</td>
                <td class="p-3 text-gray-600">{{ formatCurrency(summary.total_cop, 'COP') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  name: 'CashClosuresShow',

  components: {
    Head,
    Link,
  },

  layout: Layout,

  props: {
    cashClosure: {
      type: Object,
      required: true
    },
    transactions: {
      type: Array,
      default: () => []
    },
    summary: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    withdrawals() {
      return (this.transactions || []).filter(t => t.type === 'withdrawal')
    }
  },

  methods: {
    back() {
      this.$inertia.visit('/cash-closures')
    },

    formatCurrency(value, currency = 'USD') {
      const currencies = {
        'USD': 'en-US',
        'VES': 'es-VE',
        'COP': 'es-CO'
      }

      const symbols = {
        'USD': 'USD',
        'VES': 'VES',
        'COP': 'COP'
      }

      return new Intl.NumberFormat(currencies[currency] || 'en-US', {
        style: 'currency',
        currency: symbols[currency] || 'USD'
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getTransactionTypeClass(type) {
      const classes = {
        'opening': 'bg-blue-100 text-blue-800',
        'sale': 'bg-green-100 text-green-800',
        'payment': 'bg-green-100 text-green-800',
        'withdrawal': 'bg-red-100 text-red-800',
        'deposit': 'bg-blue-100 text-blue-800',
        'transfer_in': 'bg-purple-100 text-purple-800',
        'transfer_out': 'bg-purple-100 text-purple-800',
        'closure': 'bg-gray-100 text-gray-800',
      }
      return classes[type] || 'bg-gray-100 text-gray-800'
    },

    getTransactionTypeText(type) {
      const texts = {
        'opening': 'Apertura',
        'sale': 'Venta',
        'payment': 'Pago',
        'withdrawal': 'Retiro',
        'deposit': 'Depósito',
        'transfer_in': 'Transferencia Entrante',
        'transfer_out': 'Transferencia Saliente',
        'closure': 'Cierre',
      }
      return texts[type] || type
    },
  }
}
</script>